import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { findDateRange } from '../../../pages/Calendar/Helpers/functions';
import API from '../../API/Main/API';
import {
	AdminCreateEventProperties,
	CopyEventsProperties,
	CreateEventProperties,
	DeleteEventsProperties,
	DeleteUpcomingEventsProperties,
	DeleteUserEventProperties,
	EventDataProperties,
	GetEventsProperties,
} from '../../API/ClientAPIHelpers/calendarProperties';
import { ErrorProperties } from '../../API/identityAPIProperties';

interface CalendarStateProperties {
	error: ErrorProperties;
	event: EventDataProperties;
	events: EventDataProperties[] | null;
	isCreating: boolean;
	isDeleting: boolean;
	loading: boolean;
}

const initialEventState: EventDataProperties = {
	id: '',
	isCompleted: false,
	date: '',
	startTime: '',
	endTime: '',
	title: '',
	type: '',
	sessionType: {
		id: 0,
		name: '',
		type: 1,
		allowedRoleSections: [],
	},
	user: {
		id: '',
		fullName: '',
		firstName: '',
		lastName: '',
		email: '',
		role: {
			id: '',
			name: '',
			section: {
				id: 0,
				name: '',
			},
		},
	},
	client: {
		id: '',
		dateOfBirthday: '',
		email: '',
		fullName: '',
		gender: '',
		defaultBcba: {
			id: '',
			fullName: '',
			firstName: '',
			lastName: '',
			email: '',
			role: {
				id: '',
				name: '',
				section: {
					id: 0,
					name: '',
				},
			},
		},
	},
	sessionId: '',
	reportStatus: null,
	reportId: null,
	isSessionFlagged: false,
};

const InitialCalendarState: CalendarStateProperties = {
	isCreating: false,
	isDeleting: false,
	loading: false,
	events: null,
	event: initialEventState,
	error: {
		status: 0,
		title: '',
	},
};

export const createEvent = createAsyncThunk('/calendar/create-event', async (data: CreateEventProperties, thunkAPI) => {
	const response = await API.ClientAPI.Calendar.createEvent(data);

	if (response.error) {
		return thunkAPI.rejectWithValue(response.error);
	}

	return response as EventDataProperties;
});

export const adminCreateEvent = createAsyncThunk(
	'/calendar/admin/create-event',
	async (data: AdminCreateEventProperties, thunkAPI) => {
		const response = await API.ClientAPI.Calendar.adminCreateEvent(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as EventDataProperties;
	},
);

export const copyEvents = createAsyncThunk('/calendar/copy-events', async (data: CopyEventsProperties, thunkAPI) => {
	const response = await API.ClientAPI.Calendar.copyEvents(data);

	if (response.error) {
		return thunkAPI.rejectWithValue(response.error);
	}

	return response;
});

export const getEvents = createAsyncThunk(
	'/calendar/get-events',
	async ({ days, clientId, userId, includeBTEvents, includeBCBAEvents }: GetEventsProperties, thunkAPI) => {
		const range = findDateRange(days);
		let response: any = null;

		if (!clientId && !userId) {
			response = await API.ClientAPI.Calendar.getAllEvents({
				...range,
				includeBTEvents,
				includeBCBAEvents,
			});
		}

		if (!clientId && userId) {
			response = await API.ClientAPI.Calendar.getUserEvents({
				...range,
				userId,
			});
		}

		if (clientId && !userId) {
			response = await API.ClientAPI.Calendar.getClientEvents({
				...range,
				clientId,
			});
		}

		if (clientId && userId) {
			const clientEvents = await API.ClientAPI.Calendar.getClientEvents({
				...range,
				clientId,
			});

			if (clientEvents.error) {
				return thunkAPI.rejectWithValue(clientEvents.error);
			}
			response = clientEvents.filter((x: EventDataProperties) => x.user.id === userId);
		}

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as EventDataProperties[];
	},
);

export const getAllEventsForOneDay = createAsyncThunk(
	'/calendar/all-events-for-one-day',
	async ({ includeBTEvents, date }: { date: string; includeBTEvents?: boolean }, thunkAPI) => {
		const response = await API.ClientAPI.Calendar.getAllEvents({
			startDate: date,
			endDate: date,
			includeBTEvents,
		});

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as EventDataProperties[];
	},
);

export const deleteEvent = createAsyncThunk('/calendar/delete-event', async (eventId: string, thunkAPI) => {
	const response = await API.ClientAPI.Calendar.deleteEvent(eventId);

	if (response.error) {
		return thunkAPI.rejectWithValue(response.error);
	}

	return response as EventDataProperties[];
});

export const deleteUpcomingEvent = createAsyncThunk(
	'/calendar/delete-upcoming-event',
	async (data: DeleteUpcomingEventsProperties, thunkAPI) => {
		const response = await API.ClientAPI.Calendar.deleteUpcomingEvents(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response;
	},
);

export const deleteEvents = createAsyncThunk(
	'/calendar/delete-events',
	async (data: DeleteEventsProperties, thunkAPI) => {
		const response = await API.ClientAPI.Calendar.deleteEvents(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response;
	},
);

export const deleteUserEvent = createAsyncThunk(
	'/calendar/delete-user-event',
	async (data: DeleteUserEventProperties, thunkAPI) => {
		const response = await API.ClientAPI.Calendar.deleteUserEvent(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response;
	},
);

const CalendarSlice = createSlice({
	name: 'calendar',
	initialState: InitialCalendarState,
	reducers: {
		clearEvents(state) {
			state.events = InitialCalendarState.events;
		},
	},
	extraReducers: (builder) => {
		// deleteUserEvent
		builder.addCase(deleteUserEvent.pending, (state) => {
			state.loading = true;
		});
		builder.addCase(deleteUserEvent.fulfilled, (state) => {
			state.loading = false;
		});
		builder.addCase(deleteUserEvent.rejected, (state, action) => {
			state.loading = false;
			state.error = action.error as ErrorProperties;
		});
		// get events
		builder.addCase(getEvents.pending, (state) => {
			state.loading = true;
		});
		builder.addCase(getEvents.fulfilled, (state, action) => {
			state.loading = false;
			state.events = action.payload;
		});
		builder.addCase(getEvents.rejected, (state, action) => {
			state.loading = false;
			state.error = action.error as ErrorProperties;
		});
		// create event
		builder.addCase(createEvent.pending, (state) => {
			state.isCreating = true;
		});
		builder.addCase(createEvent.fulfilled, (state, action) => {
			state.isCreating = false;
			state.event = action.payload;
		});
		builder.addCase(createEvent.rejected, (state, action) => {
			state.isCreating = false;
			state.error = action.error as ErrorProperties;
		});

		// adminCreateEvent
		builder.addCase(adminCreateEvent.pending, (state) => {
			state.loading = true;
		});
		builder.addCase(adminCreateEvent.fulfilled, (state, action) => {
			state.loading = false;
			state.event = action.payload;
		});
		builder.addCase(adminCreateEvent.rejected, (state, action) => {
			state.loading = false;
			state.error = action.error as ErrorProperties;
		});

		// copy events
		builder.addCase(copyEvents.pending, (state) => {
			state.loading = true;
		});
		builder.addCase(copyEvents.fulfilled, (state) => {
			state.loading = false;
		});
		builder.addCase(copyEvents.rejected, (state, action) => {
			state.loading = false;
			state.error = action.error as ErrorProperties;
		});

		// delete event
		builder.addCase(deleteEvent.pending, (state) => {
			state.isDeleting = true;
		});
		builder.addCase(deleteEvent.fulfilled, (state) => {
			state.isDeleting = false;
		});
		builder.addCase(deleteEvent.rejected, (state, action) => {
			state.isDeleting = false;
			state.error = action.error as ErrorProperties;
		});

		// delete events
		builder.addCase(deleteEvents.pending, (state) => {
			state.isDeleting = true;
		});
		builder.addCase(deleteEvents.fulfilled, (state) => {
			state.isDeleting = false;
		});
		builder.addCase(deleteEvents.rejected, (state, action) => {
			state.isDeleting = false;
			state.error = action.error as ErrorProperties;
		});

		// delete upcoming events
		builder.addCase(deleteUpcomingEvent.pending, (state) => {
			state.isDeleting = true;
		});
		builder.addCase(deleteUpcomingEvent.fulfilled, (state) => {
			state.isDeleting = false;
		});
		builder.addCase(deleteUpcomingEvent.rejected, (state, action) => {
			state.isDeleting = false;
			state.error = action.error as ErrorProperties;
		});
	},
});

export const { clearEvents } = CalendarSlice.actions;
export default CalendarSlice.reducer;
